import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, CurrencyDollar, MagnifyingGlass, DownloadSimple,
  ArrowClockwise, TrendUp, Users, Receipt, CalendarBlank,
  CheckCircle, XCircle, Eye, X, Clock,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import { toast } from "sonner";
import { BRAND } from "../../lib/brand";

const STATUS_STYLE = {
  paid:       { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Pagado" },
  complete:   { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Completado" },
  initiated:  { cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "Iniciado" },
  failed:     { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Fallido" },
  pending:    { cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "Pendiente" },
  approved:   { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Aprobado" },
  rejected:   { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Rechazado" },
};

const METHOD_LABELS = { zelle: "Zelle", pago_movil: "Pago Móvil", binance: "Binance" };
const PLAN_LABELS = {
  weekly: "Semanal ($50)", monthly: "Mensual ($160)", vip: "VIP", premium: "Premium", free: "Free",
};

function ProofModal({ payment, onClose, onReview }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const review = async (action) => {
    setLoading(true);
    await onReview(payment.id, action, note);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.1] w-full max-w-lg p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-lg">Revisar Comprobante</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-zinc-500">Usuario:</span>
            <span className="font-semibold">{payment.user_name} · {payment.user_email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Método:</span>
            <span className="font-bold text-yellow-400">{METHOD_LABELS[payment.payment_method] || payment.payment_method}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Plan:</span>
            <span className="font-semibold">{PLAN_LABELS[payment.plan_id] || payment.plan_id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Monto:</span>
            <span className="font-black text-emerald-400 text-base">${payment.amount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Fecha:</span>
            <span className="text-zinc-400 font-mono text-xs">{payment.created_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
        </div>

        {payment.payment_proof_image ? (
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-2">Comprobante de pago</p>
            <img
              src={payment.payment_proof_image}
              alt="Comprobante"
              className="w-full max-h-64 object-contain rounded-xl border border-white/[0.08] bg-black"
            />
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400 text-center">
            Sin comprobante adjunto
          </div>
        )}

        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">
            Nota para el cliente (opcional)
          </label>
          <input
            className="input-field w-full text-sm"
            placeholder="ej: Pago recibido correctamente / Monto incorrecto..."
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        {payment.status === "pending" && (
          <div className="flex gap-2">
            <button
              onClick={() => review("reject")}
              disabled={loading}
              data-testid="review-reject-btn"
              className="flex-1 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-bold hover:bg-red-500/20 transition-all disabled:opacity-50"
            >
              <XCircle className="w-4 h-4 inline mr-1.5" />
              Rechazar
            </button>
            <button
              onClick={() => review("approve")}
              disabled={loading}
              data-testid="review-approve-btn"
              className="flex-1 py-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-sm font-bold hover:bg-emerald-500/20 transition-all disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4 inline mr-1.5" />
              {loading ? "Procesando..." : "Aprobar"}
            </button>
          </div>
        )}
        {payment.status !== "pending" && (
          <div className={`text-center py-2 rounded-lg text-sm font-bold ${
            payment.status === "approved" ? "text-emerald-400 bg-emerald-500/10" : "text-red-400 bg-red-500/10"
          }`}>
            {payment.status === "approved" ? "Pago aprobado — Suscripción activa" : "Pago rechazado"}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("stripe");
  const [proofModal, setProofModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, mp] = await Promise.all([
        apiClient.get("/admin/payments?limit=500"),
        apiClient.get("/admin/payments/stats"),
        apiClient.get("/admin/payments/manual"),
      ]);
      setPayments(p.data.payments || []);
      setStats(s.data);
      setManualPayments(mp.data.payments || []);
    } catch (err) {
      console.error("[AdminPayments] load failed:", err?.response?.data || err.message);
      toast.error("Error al cargar pagos");
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (paymentId, action, adminNote) => {
    try {
      const res = await apiClient.post(`/admin/payments/manual/${paymentId}/review`, { action, admin_note: adminNote });
      toast.success(res.data.message || (action === "approve" ? "Pago aprobado" : "Pago rechazado"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar");
      throw err;
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/payments/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${BRAND.slug}-pagos-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch {
      toast.error("Error al exportar");
    }
    setExporting(false);
  };

  const pendingManual = manualPayments.filter(p => p.status === "pending").length;

  const filtered = payments.filter(p => {
    const matchSearch =
      !search ||
      (p.user_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.user_email || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || p.status === filterStatus || p.payment_status === filterStatus;
    return matchSearch && matchStatus;
  });

  const filteredManual = manualPayments.filter(p => {
    const matchSearch =
      !search ||
      (p.user_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.user_email || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-background">
      {proofModal && (
        <ProofModal
          payment={proofModal}
          onClose={() => setProofModal(null)}
          onReview={handleReview}
        />
      )}

      {/* Sticky header */}
      <div className="border-b border-white/[0.06] bg-surface/50 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-500 hover:text-foreground transition-colors">
              <CaretLeft weight="bold" className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <CurrencyDollar weight="duotone" className="w-5 h-5 text-yellow-400" />
              <span className="font-bold text-lg">Historial de Pagos</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="payments-refresh-btn"
              className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/[0.05] transition-all"
            >
              <ArrowClockwise className="w-4 h-4" />
            </button>
            <button
              onClick={exportCSV}
              disabled={exporting}
              data-testid="payments-export-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 transition-all text-sm font-semibold disabled:opacity-50"
            >
              <DownloadSimple className="w-4 h-4" />
              {exporting ? "Exportando..." : "Exportar CSV"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: "Revenue total",
              value: `$${(stats?.revenue_total || 0).toFixed(0)}`,
              icon: TrendUp,
              color: "text-yellow-400",
              bg: "bg-yellow-500/10",
              border: "border-yellow-500/15",
              testId: "payment-stat-total",
            },
            {
              label: "Revenue este mes",
              value: `$${(stats?.revenue_month || 0).toFixed(0)}`,
              icon: CalendarBlank,
              color: "text-emerald-400",
              bg: "bg-emerald-500/10",
              border: "border-emerald-500/15",
              testId: "payment-stat-month",
            },
            {
              label: "Transacciones",
              value: stats?.total_transactions ?? 0,
              icon: Receipt,
              color: "text-blue-400",
              bg: "bg-blue-500/10",
              border: "border-blue-500/15",
              testId: "payment-stat-transactions",
            },
            {
              label: "Pendientes manuales",
              value: pendingManual,
              icon: Clock,
              color: pendingManual > 0 ? "text-yellow-400" : "text-zinc-500",
              bg: pendingManual > 0 ? "bg-yellow-500/10" : "bg-white/[0.03]",
              border: pendingManual > 0 ? "border-yellow-500/15" : "border-white/[0.06]",
              testId: "payment-stat-manual-pending",
            },
          ].map(s => (
            <div
              key={s.label}
              data-testid={s.testId}
              className={`surface rounded-xl p-4 border ${s.border}`}
            >
              <div className={`${s.bg} w-8 h-8 rounded-lg flex items-center justify-center mb-3`}>
                <s.icon weight="duotone" className={`w-4 h-4 ${s.color}`} />
              </div>
              <div className={`text-2xl font-black number-ticker ${s.color}`}>{s.value}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Month comparison bar */}
        {stats && (
          <div className="surface rounded-xl p-4 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">Mes anterior:</span>
              <span className="font-bold text-zinc-300">${(stats.revenue_last_month || 0).toFixed(0)}</span>
            </div>
            <div className="w-px h-4 bg-white/[0.08]" />
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">Promedio por usuario:</span>
              <span className="font-bold text-yellow-400">${(stats.avg_per_user || 0).toFixed(0)}</span>
            </div>
            {stats.revenue_month > 0 && stats.revenue_last_month > 0 && (
              <>
                <div className="w-px h-4 bg-white/[0.08]" />
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-500">Variación mes:</span>
                  <span className={`font-bold ${stats.revenue_month >= stats.revenue_last_month ? "text-emerald-400" : "text-red-400"}`}>
                    {stats.revenue_month >= stats.revenue_last_month ? "+" : ""}
                    {(((stats.revenue_month - stats.revenue_last_month) / stats.revenue_last_month) * 100).toFixed(1)}%
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1.5 border-b border-white/[0.06] pb-0">
          {[
            { id: "stripe", label: "Stripe / Tarjeta" },
            {
              id: "manual",
              label: "Pagos Manuales",
              badge: pendingManual > 0 ? pendingManual : null,
            },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`payments-tab-${tab.id}`}
              onClick={() => { setActiveTab(tab.id); setSearch(""); setFilterStatus("all"); }}
              className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
                activeTab === tab.id
                  ? "border-yellow-400 text-yellow-400"
                  : "border-transparent text-zinc-500 hover:text-white"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="w-5 h-5 rounded-full bg-yellow-400 text-black text-[10px] font-black flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-0 max-w-sm">
            <MagnifyingGlass className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="payments-search"
              className="input-field pl-10 w-full"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-1.5">
            {activeTab === "stripe" ? [
              { value: "all", label: "Todos" },
              { value: "paid", label: "Pagados" },
              { value: "initiated", label: "Iniciados" },
            ].map(f => (
              <button key={f.value} data-testid={`payments-filter-${f.value}`}
                onClick={() => setFilterStatus(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  filterStatus === f.value
                    ? "bg-yellow-500/15 border-yellow-500/25 text-yellow-400"
                    : "border-white/[0.08] text-zinc-500 hover:text-white hover:border-white/[0.15]"
                }`}
              >{f.label}</button>
            )) : [
              { value: "all", label: "Todos" },
              { value: "pending", label: "Pendientes" },
              { value: "approved", label: "Aprobados" },
              { value: "rejected", label: "Rechazados" },
            ].map(f => (
              <button key={f.value} data-testid={`manual-filter-${f.value}`}
                onClick={() => setFilterStatus(f.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  filterStatus === f.value
                    ? "bg-yellow-500/15 border-yellow-500/25 text-yellow-400"
                    : "border-white/[0.08] text-zinc-500 hover:text-white hover:border-white/[0.15]"
                }`}
              >{f.label}</button>
            ))}
          </div>
        </div>

        {/* Tables */}
        {activeTab === "stripe" ? (
          <div className="surface rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-zinc-500">Cargando pagos...</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <CurrencyDollar className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
                <p className="text-zinc-500">No se encontraron transacciones</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      {["Fecha", "Usuario", "Plan", "Monto", "Estado", "Session ID"].map(h => (
                        <th key={h} className="px-5 py-3.5 text-left text-[11px] text-zinc-500 uppercase tracking-wider font-bold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {filtered.map((p, i) => {
                      const status = p.payment_status === "paid" ? "paid" : p.status;
                      const stS = STATUS_STYLE[status] || STATUS_STYLE.initiated;
                      return (
                        <tr key={p.id || p.session_id || i} data-testid={`payment-row-${i}`}
                          className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="text-xs text-zinc-400 font-mono">{p.created_at ? p.created_at.slice(0, 10) : "—"}</span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold truncate max-w-[180px]">{p.user_name || "—"}</div>
                            <div className="text-xs text-zinc-500 truncate max-w-[180px]">{p.user_email || "—"}</div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="chip text-[11px] border border-white/[0.08] bg-white/[0.03] text-zinc-400">
                              {PLAN_LABELS[p.plan_id] || p.plan_id || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="font-black text-emerald-400 text-base">${(p.amount || 0).toFixed(2)}</span>
                            <span className="text-[10px] text-zinc-600 ml-1 uppercase">{p.currency || "usd"}</span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className={`chip text-[11px] border ${stS.cls}`}>{stS.label}</span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-[10px] text-zinc-600 font-mono truncate block max-w-[160px]">
                              {p.session_id ? p.session_id.slice(0, 24) + "..." : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="surface rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-zinc-500">Cargando pagos manuales...</div>
            ) : filteredManual.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
                <p className="text-zinc-500">No hay pagos manuales {filterStatus !== "all" ? `con estado "${filterStatus}"` : ""}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      {["Fecha", "Usuario", "Método", "Plan", "Monto", "Estado", "Acción"].map(h => (
                        <th key={h} className="px-5 py-3.5 text-left text-[11px] text-zinc-500 uppercase tracking-wider font-bold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {filteredManual.map((p, i) => {
                      const stS = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
                      return (
                        <tr key={p.id || i} data-testid={`manual-payment-row-${i}`}
                          className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="text-xs text-zinc-400 font-mono">{p.created_at ? p.created_at.slice(0, 10) : "—"}</span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold truncate max-w-[160px]">{p.user_name || "—"}</div>
                            <div className="text-xs text-zinc-500 truncate max-w-[160px]">{p.user_email || "—"}</div>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="chip text-[11px] border border-yellow-500/20 bg-yellow-500/10 text-yellow-400">
                              {METHOD_LABELS[p.payment_method] || p.payment_method}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="chip text-[11px] border border-white/[0.08] bg-white/[0.03] text-zinc-400">
                              {PLAN_LABELS[p.plan_id] || p.plan_id}
                            </span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="font-black text-emerald-400 text-base">${(p.amount || 0).toFixed(2)}</span>
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className={`chip text-[11px] border ${stS.cls}`}>{stS.label}</span>
                          </td>
                          <td className="px-5 py-4">
                            <button
                              onClick={() => setProofModal(p)}
                              data-testid={`manual-review-btn-${i}`}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all bg-white/[0.03] border-white/[0.1] text-zinc-300 hover:text-white hover:border-white/[0.2]"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Ver comprobante
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "stripe" && filtered.length > 0 && (
          <p className="text-xs text-zinc-600 text-right">
            {filtered.length} de {payments.length} transacciones
          </p>
        )}
      </div>
    </div>
  );
}

